/**
 * AddSchoolModal.tsx
 * Modal/form para criar uma nova escola demo.
 * Usuário informa apenas nome + nAlunos. Tudo mais é calculado automaticamente.
 */
import React, { useState, useCallback } from 'react';
import { simulationApi } from '../../services/simulationApi';
import type { DemoSchool } from '../../types';

interface AddSchoolModalProps {
  municipioId: string;    // ID do município selecionado (P1)
  onCreated: (school: DemoSchool) => void;
  onClose: () => void;
}

// Cálculo de preview
function calcPreview(nAlunos: number) {
  const turmas       = Math.ceil(nAlunos / 30);
  const professores  = turmas;
  const coordenadores = 1;
  const diretores    = 1;
  const secretaria   = 1;
  const responsaveis = Math.min(nAlunos, 200);
  return { turmas, professores, coordenadores, diretores, secretaria, responsaveis };
}

function PreviewBadge({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5 flex-1 min-w-[60px]">
      <span className="text-[18px] font-bold" style={{ color }}>{value}</span>
      <span className="text-[8px] text-[#6c7086] text-center leading-tight">{label}</span>
    </div>
  );
}

export function AddSchoolModal({ municipioId, onCreated, onClose }: AddSchoolModalProps) {
  const [nome, setNome]       = useState('');
  const [nAlunos, setNAlunos] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const preview = calcPreview(nAlunos);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) { setError('Informe o nome da escola.'); return; }
    setLoading(true);
    setError(null);
    try {
      const school = await simulationApi.createDemoSchool(nome.trim(), municipioId, nAlunos);
      onCreated(school);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Erro ao criar escola. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [nome, nAlunos, municipioId, onCreated]);

  return (
    /* Overlay */
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Card */}
      <form
        className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 shadow-2xl flex flex-col gap-0 overflow-hidden"
        style={{ background: 'linear-gradient(160deg,#1e1e2e 0%,#181825 100%)' }}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-bold text-white">Nova Escola Demo</h2>
            <p className="text-[9px] text-[#6c7086] mt-0.5">
              Informe o nome e o nº de alunos — o resto é calculado automaticamente.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#6c7086] hover:text-white transition-colors w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/5"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-4">

          {/* Nome */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[9px] font-bold text-[#a6adc8] uppercase tracking-widest">
              Nome da Escola
            </label>
            <input
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: E.M. João Pessoa"
              maxLength={80}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white placeholder-[#45475a] outline-none focus:border-[#cba6f7]/60 focus:bg-white/8 transition-all"
              autoFocus
            />
          </div>

          {/* Nº de Alunos */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-[9px] font-bold text-[#a6adc8] uppercase tracking-widest">
                Nº de Alunos
              </label>
              <span className="text-[14px] font-bold text-[#cba6f7]">{nAlunos}</span>
            </div>
            <input
              type="range"
              min={10}
              max={500}
              step={10}
              value={nAlunos}
              onChange={e => setNAlunos(Number(e.target.value))}
              className="w-full accent-[#cba6f7] cursor-pointer"
            />
            <div className="flex justify-between text-[8px] text-[#45475a]">
              <span>10</span>
              <span>250</span>
              <span>500</span>
            </div>
          </div>

          {/* Preview calculado */}
          <div
            className="rounded-xl border border-white/8 px-3 py-3"
            style={{ background: 'rgba(203,166,247,0.05)' }}
          >
            <div className="text-[8px] font-bold text-[#cba6f7]/60 uppercase tracking-widest mb-3">
              Gerado automaticamente
            </div>
            <div className="flex justify-between flex-wrap gap-2">
              <PreviewBadge label="Turmas"      value={preview.turmas}       color="#89dceb" />
              <PreviewBadge label="Professores" value={preview.professores}  color="#89b4fa" />
              <PreviewBadge label="Diretor"     value={preview.diretores}    color="#a6e3a1" />
              <PreviewBadge label="Coordenação" value={preview.coordenadores} color="#b4befe" />
              <PreviewBadge label="Secretaria"  value={preview.secretaria}   color="#94e2d5" />
              <PreviewBadge label="Responsáveis" value={preview.responsaveis} color="#cba6f7" />
            </div>
          </div>

          {/* Erro */}
          {error && (
            <div className="text-[10px] text-[#f38ba8] bg-[#f38ba8]/10 border border-[#f38ba8]/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/8 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2 rounded-xl border border-white/10 text-[11px] text-[#a6adc8] hover:bg-white/5 transition-all disabled:opacity-40 cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || !nome.trim()}
            className="flex-[2] py-2 rounded-xl text-[11px] font-bold text-white transition-all disabled:opacity-40 cursor-pointer flex items-center justify-center gap-2"
            style={{
              background: loading || !nome.trim()
                ? 'rgba(203,166,247,0.15)'
                : 'linear-gradient(135deg,#cba6f7 0%,#89b4fa 100%)',
            }}
          >
            {loading ? (
              <>
                <span className="animate-spin text-base">⏳</span>
                Criando escola…
              </>
            ) : (
              <>🏫 Criar Escola Demo</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
